<template>
  <div class="organization-settings">
    <div class="container">
      <PageHeader title="Organization" description="Manage your organization settings" />
      <Tabs v-model="activeTab" class="organization-settings__tabs">
        <Tab name="general" title="General">
          <div class="organization-settings__form">
            <Card title="Organisatiegegevens">
              <div class="organization-settings__row">
                <TextInput
                  id="org-name"
                  label="Organisatienaam"
                  v-model="inputs.name.binding"
                  @blur="handleBlur('name')"
                  @update:modelValue="handleChange('name')"
                />
                <SelectInput
                  id="org-language"
                  label="Taal"
                  v-model="inputs.language.binding"
                  :options="[
                    { value: 'en-US', label: 'English' },
                    { value: 'nl-NL', label: 'Nederlands' },
                  ]"
                  @blur="handleBlur('language')"
                  @update:modelValue="handleChange('language')"
                />
              </div>
            </Card>

            <Card title="Adresgegevens">
              <div class="organization-settings__row">
                <TextInput
                  id="org-street"
                  :label="inputs.street_address.label"
                  v-model="inputs.street_address.binding"
                  @blur="handleBlur('street_address')"
                  @update:modelValue="handleChange('street_address')"
                />
                <TextInput
                  id="org-city"
                  label="Plaats"
                  v-model="inputs.city.binding"
                  @blur="handleBlur('city')"
                  @update:modelValue="handleChange('city')"
                />
              </div>
              <div class="organization-settings__row">
                <TextInput
                  id="org-state"
                  :label="inputs.state.label"
                  v-model="inputs.state.binding"
                  @blur="handleBlur('state')"
                  @update:modelValue="handleChange('state')"
                />
                <TextInput
                  id="org-postal"
                  :label="inputs.postal_code.label"
                  v-model="inputs.postal_code.binding"
                  @blur="handleBlur('postal_code')"
                  @update:modelValue="handleChange('postal_code')"
                />
              </div>
              <div class="organization-settings__row">
                <SelectInput
                  id="org-country"
                  :label="inputs.country.label"
                  v-model="inputs.country.binding"
                  :options="[
                    { value: 'NL', label: 'Nederland' },
                    { value: 'BE', label: 'België' },
                    { value: 'FR', label: 'Frankrijk' },
                    { value: 'DE', label: 'Duitsland' },
                    { value: 'IT', label: 'Italië' },
                    { value: 'ES', label: 'Spanje' },
                    { value: 'GB', label: 'Verenigd Koninkrijk' },
                    { value: 'CA', label: 'Canada' },
                    { value: 'US', label: 'Verenigde Staten' },
                    { value: 'other', label: 'Overig' },
                  ]"
                  @blur="handleBlur('country')"
                  @update:modelValue="handleChange('country')"
                />
                <TextInput
                  id="org-phone"
                  :label="inputs.phone.label"
                  v-model="inputs.phone.binding"
                  @blur="handleBlur('phone')"
                  @update:modelValue="handleChange('phone')"
                />
              </div>
            </Card>

            <Card title="Bedrijfs- en bankgegevens">
              <div class="organization-settings__row">
                <TextInput
                  id="org-kvk"
                  :label="inputs.registration_number.label"
                  v-model="inputs.registration_number.binding"
                  @blur="handleBlur('registration_number')"
                  @update:modelValue="handleChange('registration_number')"
                />
                <TextInput
                  id="org-vat"
                  :label="inputs.vat_number.label"
                  v-model="inputs.vat_number.binding"
                  @blur="handleBlur('vat_number')"
                  @update:modelValue="handleChange('vat_number')"
                />
              </div>
              <div class="organization-settings__row">
                <TextInput
                  id="org-iban"
                  :label="inputs.bank_account.label"
                  v-model="inputs.bank_account.binding"
                  @blur="handleBlur('bank_account')"
                  @update:modelValue="handleChange('bank_account')"
                  placeholder="NL00BANK0000000000"
                />
                <div class="organization-settings__field">
                  <TextInput
                    id="org-bic"
                    :label="inputs.bic.label"
                    v-model="inputs.bic.binding"
                    @blur="handleBlur('bic')"
                    @update:modelValue="handleChange('bic')"
                    placeholder="INGBNL2A"
                  />
                  <p class="organization-settings__help">
                    SWIFT/BIC code, doorgaans 8 of 11 tekens.
                  </p>
                </div>
              </div>
            </Card>

            <Card
              title="Algemene voorwaarden"
              subtitle="Configureer de URL naar de algemene voorwaarden die klanten moeten accepteren bij het aanvragen van een offerte."
            >
              <div class="organization-settings__row organization-settings__row--single">
                <TextInput
                  id="org-terms"
                  label="URL naar algemene voorwaarden"
                  v-model="inputs.terms_url.binding"
                  @blur="handleBlur('terms_url')"
                  @update:modelValue="handleChange('terms_url')"
                  placeholder="https://example.com/algemene-voorwaarden"
                />
              </div>
            </Card>
          </div>
        </Tab>
        <Tab name="team" title="Team">
          <div class="member-list">
            <div class="member-list__header">
              <h3>Active Members</h3>
              <Btn
                text="Invite team member"
                @click="openInviteMemberModal"
                icon="plus"
                variant="secondary"
              />
            </div>
            <div v-if="teamMembers.length > 0">
              <div v-for="member in teamMembers" :key="member.id" class="member-item">
                <div class="member-info">
                  <AvatarElement
                    :title="
                      member.user.first_name
                        ? member.user.first_name?.charAt(0) + member.user.last_name?.charAt(0)
                        : ''
                    "
                    :alt="member.user.first_name + ' ' + member.user.last_name"
                  />
                  <div>
                    <h4>
                      {{
                        member.user.first_name
                          ? member.user.first_name + ' ' + member.user.last_name
                          : member.email
                      }}
                    </h4>
                    <p>{{ member.user.email }}</p>
                    <p
                      v-if="member.role !== 'super_admin'"
                      class="member-hours-summary"
                      :class="{
                        'member-hours-summary--custom': hasMemberOverrides(
                          member.planning_settings
                        ),
                      }"
                    >
                      <BaseIcon name="clock" />
                      {{ summarizeMemberHours(member.planning_settings, planningWorkTime) }}
                    </p>
                  </div>
                </div>
                <div class="member-actions">
                  <Btn
                    v-if="member.role !== 'super_admin'"
                    variant="plain"
                    size="sm"
                    icon="clock"
                    text="Werktijden"
                    @click="openMemberWorkHoursModal(member)"
                  />
                  <div
                    v-if="member.role === 'super_admin' || isCurrentUser(member)"
                    class="member-role"
                    :class="'member-role--' + member.role"
                  >
                    {{ getRoleLabel(member.role) }}
                  </div>
                  <select
                    v-else
                    class="member-role-select"
                    :class="'member-role-select--' + member.role"
                    :value="member.role"
                    :disabled="updatingMemberRoleId === member.id"
                    @change="handleMemberRoleChange(member, $event.target.value)"
                  >
                    <option
                      v-for="option in assignableRoles"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div v-else class="member-list__empty">
              <EmptyState
                icon="users"
                :title="$t('settings.team_no_members_title')"
                :description="$t('settings.team_no_members')"
                size="sm"
              />
            </div>
          </div>

          <div v-if="pendingInvites.length > 0" class="pending-invites">
            <h3>Pending Invites</h3>
            <div v-for="invite in pendingInvites" :key="invite.id" class="invite-item">
              <div class="invite-info">
                <p>{{ invite.email }}</p>
                <span class="invite-status">Pending</span>
              </div>
              <div class="invite-actions">
                <Btn
                  :text="getResendButtonText(invite.id)"
                  variant="secondary"
                  size="sm"
                  @click="resendInvite(invite.id)"
                  :loading="resendingInviteId === invite.id"
                  :disabled="isResendDisabled(invite.id)"
                />
                <Btn
                  text="Cancel"
                  variant="danger"
                  size="sm"
                  @click="cancelInvite(invite.id)"
                  :loading="cancellingInviteId === invite.id"
                />
              </div>
            </div>
          </div>
        </Tab>
        <Tab name="brand" title="Brand">
          <div class="settings-section">
            <h3>Brand Settings</h3>
            <div class="form-grid">
              <ColorPicker
                :label="inputs.primary_color.label"
                v-model="inputs.primary_color.binding"
                @blur="handleBlur('primary_color')"
                @update:modelValue="handleChange('primary_color')"
              />
              <ColorPicker
                :label="inputs.primary_accent_color.label"
                v-model="inputs.primary_accent_color.binding"
                @blur="handleBlur('primary_accent_color')"
                @update:modelValue="handleChange('primary_accent_color')"
              />
              <FileInput
                :label="inputs.logo.label"
                accept="image/*"
                v-model="inputs.logo.binding"
                :is-uploading="isLogoUploading"
                :upload-config="{
                  type: 'logo',
                  organizationId: organization.id,
                  context: 'organization',
                }"
                @update:modelValue="handleChange('logo')"
              />
              <FileInput
                :label="inputs.icon.label"
                accept="image/*"
                v-model="inputs.icon.binding"
                :is-uploading="isIconUploading"
                :upload-config="{
                  type: 'icon',
                  organizationId: organization.id,
                  context: 'organization',
                }"
                @update:modelValue="handleChange('icon')"
              />
            </div>
          </div>
        </Tab>
        <Tab name="crm" title="CRM">
          <div class="settings-section">
            <h3>Default CRM Settings</h3>
            <div class="form-grid">
              <TextInput
                :label="inputs.reply_to_email.label"
                v-model="inputs.reply_to_email.binding"
                @blur="handleBlur('reply_to_email')"
                @update:modelValue="handleChange('reply_to_email')"
                placeholder="noreply@example.com"
                type="email"
              />
              <TextInput
                :label="inputs.proposal_mail_subject.label"
                v-model="inputs.proposal_mail_subject.binding"
                @blur="handleBlur('proposal_mail_subject')"
                @update:modelValue="handleChange('proposal_mail_subject')"
              />
              <WysiwygEditor
                :label="inputs.proposal_mail_body.label"
                v-model="inputs.proposal_mail_body.binding"
                @blur="handleBlur('proposal_mail_body')"
                @update:modelValue="handleChange('proposal_mail_body')"
              />
              <SenderSignaturePreview :body="inputs.proposal_mail_body.binding" />
              <WysiwygEditor
                :label="inputs.proposal_introduction.label"
                v-model="inputs.proposal_introduction.binding"
                @blur="handleBlur('proposal_introduction')"
                @update:modelValue="handleChange('proposal_introduction')"
              />
              <MessageElement>
                Available variables: [lead_first_name], [lead_last_name], [proposal_title],
                [organization_name], [handtekening_gebruiker]
              </MessageElement>
              <ToggleInput
                id="org-deal-email-enabled"
                v-model="inputs.deal_email_enabled.binding"
                :label="$t('settings.deal_email.label')"
                @update:modelValue="handleChange('deal_email_enabled')"
              />
              <p class="section-description">
                {{ $t('settings.deal_email.help') }}
              </p>
              <ToggleInput
                id="org-show-sender-contact"
                v-model="inputs.show_sender_contact.binding"
                :label="$t('settings.sender_contact.label')"
                @update:modelValue="handleChange('show_sender_contact')"
              />
              <p class="section-description">
                {{ $t('settings.sender_contact.help') }}
              </p>
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>{{ $t('settings.appointment_email_notice.title') }}</h3>
            <p class="section-description">
              {{ $t('settings.appointment_email_notice.description') }}
            </p>
            <div class="form-grid">
              <WysiwygEditor
                :label="inputs.appointment_email_notice.label"
                v-model="inputs.appointment_email_notice.binding"
                @blur="handleBlur('appointment_email_notice')"
                @update:modelValue="handleChange('appointment_email_notice')"
              />
              <MessageElement>
                {{ $t('settings.appointment_email_notice.variables') }}
              </MessageElement>
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>WhatsApp bericht</h3>
            <p class="section-description">
              Standaard WhatsApp bericht dat wordt gebruikt bij het verzenden van offertes via
              WhatsApp.
            </p>
            <div class="form-grid">
              <TextAreaInput
                label="WhatsApp berichttemplate"
                v-model="inputs.whatsapp_proposal_message.binding"
                @blur="handleBlur('whatsapp_proposal_message')"
                @update:modelValue="handleChange('whatsapp_proposal_message')"
                :rows="6"
                placeholder="Beste [lead_first_name],&#10;&#10;Hierbij ontvangt u de offerte van [organization_name].&#10;&#10;[proposal_link]"
              />
              <MessageElement>
                Beschikbare variabelen: [lead_first_name], [lead_last_name], [organization_name],
                [proposal_link]
              </MessageElement>
            </div>
          </div>
        </Tab>
        <Tab name="proposals" title="Offertes">
          <!-- Proposal Workflow -->
          <div class="settings-section">
            <h3>{{ $t('settings.proposal_workflow') }}</h3>
            <div class="form-grid">
              <div class="form-group">
                <RadioGroup
                  id="proposal_workflow"
                  v-model="inputs.proposal_workflow.binding"
                  :options="workflowOptions"
                  @change="handleChange('proposal_workflow')"
                />
              </div>
              <div
                v-if="inputs.proposal_workflow.binding === 'measurement'"
                class="workflow-sub-settings"
              >
                <div class="form-group">
                  <RadioGroup
                    id="price_deviation_mode"
                    :label="$t('settings.price_deviation.title')"
                    v-model="inputs.price_deviation_mode.binding"
                    :options="[
                      { value: 'hold', label: $t('settings.price_deviation.mode_hold') },
                      {
                        value: 'adjust_accept',
                        label: $t('settings.price_deviation.mode_adjust_accept'),
                      },
                      { value: 'off', label: $t('settings.price_deviation.mode_off') },
                    ]"
                    @change="handleChange('price_deviation_mode')"
                  />
                  <p class="help-text">
                    {{
                      inputs.price_deviation_mode.binding
                        ? $t(`settings.price_deviation.help_${inputs.price_deviation_mode.binding}`)
                        : $t('settings.price_deviation.help_legacy')
                    }}
                  </p>
                </div>
                <div
                  v-if="
                    inputs.price_deviation_mode.binding &&
                    inputs.price_deviation_mode.binding !== 'off'
                  "
                  class="form-group"
                >
                  <NumberInput
                    :label="$t('settings.price_deviation.max_percent')"
                    v-model="inputs.price_deviation_max_percent.binding"
                    @blur="handleBlur('price_deviation_max_percent')"
                    @update:modelValue="handleChange('price_deviation_max_percent')"
                    :min="0"
                    :max="100"
                    suffix="%"
                  />
                  <p class="help-text">{{ $t('settings.price_deviation.max_percent_help') }}</p>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>Prijzen in productcatalogus</h3>
            <p class="section-description">
              Hoe worden de prijzen in jullie productcatalogus opgeslagen? Deze instelling geldt als
              standaard voor alle producten — per product is een override mogelijk via "Geavanceerd"
              in de productdetails.
            </p>
            <div class="vat-basis-options">
              <label
                class="vat-basis-option"
                :class="{
                  'vat-basis-option--checked': inputs.proposal_prices_include_vat.binding === true,
                }"
              >
                <input
                  type="radio"
                  name="vat-basis"
                  :checked="inputs.proposal_prices_include_vat.binding === true"
                  @change="handleVatBasisChange(true)"
                />
                <div class="vat-basis-option__body">
                  <span class="vat-basis-option__title">Inclusief BTW</span>
                  <span class="vat-basis-option__hint">
                    Consument-stijl (standaard). De BTW zit al verwerkt in de prijs die je invoert.
                  </span>
                </div>
              </label>
              <label
                class="vat-basis-option"
                :class="{
                  'vat-basis-option--checked': inputs.proposal_prices_include_vat.binding === false,
                }"
              >
                <input
                  type="radio"
                  name="vat-basis"
                  :checked="inputs.proposal_prices_include_vat.binding === false"
                  @change="handleVatBasisChange(false)"
                />
                <div class="vat-basis-option__body">
                  <span class="vat-basis-option__title">Exclusief BTW</span>
                  <span class="vat-basis-option__hint">
                    B2B-stijl. De BTW wordt bovenop de prijs opgeteld.
                  </span>
                </div>
              </label>
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>Offertepresentatie</h3>
            <p class="section-description">
              Hoe worden prijzen op offertes aan de klant getoond? Dit staat los van hoe de prijzen
              in de productcatalogus zijn opgeslagen — de bedragen worden automatisch omgerekend.
              Per offerte kun je hiervan afwijken in de offerte-editor.
            </p>
            <div class="vat-basis-options">
              <label
                class="vat-basis-option"
                :class="{
                  'vat-basis-option--checked': inputs.proposal_display_basis.binding === 'incl',
                }"
              >
                <input
                  type="radio"
                  name="proposal-display-basis"
                  :checked="inputs.proposal_display_basis.binding === 'incl'"
                  @change="handleProposalBasisChange('incl')"
                />
                <div class="vat-basis-option__body">
                  <span class="vat-basis-option__title">Inclusief BTW</span>
                  <span class="vat-basis-option__hint">
                    Consument-stijl (standaard). Regelprijzen worden incl. btw getoond, met de btw
                    als specificatie onderaan.
                  </span>
                </div>
              </label>
              <label
                class="vat-basis-option"
                :class="{
                  'vat-basis-option--checked': inputs.proposal_display_basis.binding === 'excl',
                }"
              >
                <input
                  type="radio"
                  name="proposal-display-basis"
                  :checked="inputs.proposal_display_basis.binding === 'excl'"
                  @change="handleProposalBasisChange('excl')"
                />
                <div class="vat-basis-option__body">
                  <span class="vat-basis-option__title">Exclusief BTW</span>
                  <span class="vat-basis-option__hint">
                    B2B-stijl. Regelprijzen worden excl. btw getoond en de btw wordt onderaan
                    opgeteld.
                  </span>
                </div>
              </label>
            </div>
            <p class="section-description" style="margin-top: 1.5rem">
              Welke regelbedragen ziet de klant op de offerte?
            </p>
            <div class="vat-basis-options">
              <label
                class="vat-basis-option"
                :class="{
                  'vat-basis-option--checked':
                    inputs.proposal_price_visibility.binding === 'line_items',
                }"
              >
                <input
                  type="radio"
                  name="proposal-price-visibility"
                  :checked="inputs.proposal_price_visibility.binding === 'line_items'"
                  @change="handlePriceVisibilityChange('line_items')"
                />
                <div class="vat-basis-option__body">
                  <span class="vat-basis-option__title">Alle regelprijzen</span>
                  <span class="vat-basis-option__hint">
                    Stukprijs en totaal op elke regel (standaard).
                  </span>
                </div>
              </label>
              <label
                class="vat-basis-option"
                :class="{
                  'vat-basis-option--checked':
                    inputs.proposal_price_visibility.binding === 'main_products',
                }"
              >
                <input
                  type="radio"
                  name="proposal-price-visibility"
                  :checked="inputs.proposal_price_visibility.binding === 'main_products'"
                  @change="handlePriceVisibilityChange('main_products')"
                />
                <div class="vat-basis-option__body">
                  <span class="vat-basis-option__title">Alleen totaal per hoofdproduct</span>
                  <span class="vat-basis-option__hint">
                    Upsells blijven zichtbaar zonder prijs; hun bedrag telt bij het hoofdproduct.
                  </span>
                </div>
              </label>
              <label
                class="vat-basis-option"
                :class="{
                  'vat-basis-option--checked':
                    inputs.proposal_price_visibility.binding === 'configuration_subtotals',
                }"
              >
                <input
                  type="radio"
                  name="proposal-price-visibility"
                  :checked="inputs.proposal_price_visibility.binding === 'configuration_subtotals'"
                  @change="handlePriceVisibilityChange('configuration_subtotals')"
                />
                <div class="vat-basis-option__body">
                  <span class="vat-basis-option__title">Subtotaal per configuratie</span>
                  <span class="vat-basis-option__hint">
                    Elke regel houdt zijn prijs. Onder elke configuratie staat wat die ruimte
                    bij elkaar kost, inclusief upsells en opties.
                  </span>
                </div>
              </label>
              <label
                class="vat-basis-option"
                :class="{
                  'vat-basis-option--checked':
                    inputs.proposal_price_visibility.binding === 'grand_total',
                }"
              >
                <input
                  type="radio"
                  name="proposal-price-visibility"
                  :checked="inputs.proposal_price_visibility.binding === 'grand_total'"
                  @change="handlePriceVisibilityChange('grand_total')"
                />
                <div class="vat-basis-option__body">
                  <span class="vat-basis-option__title">Alleen eindtotaal</span>
                  <span class="vat-basis-option__hint">
                    Geen bedragen op regels; alleen het totalenblok onderaan.
                  </span>
                </div>
              </label>
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>Prijsafronding configurator</h3>
            <p class="section-description">
              Rond automatisch elke productprijs af die via de configurator op een offerte of
              (definitieve) order terechtkomt. Afronding gebeurt per regel op het bedrag inclusief
              btw. Handmatig aangemaakte offertes en facturen worden nooit afgerond.
            </p>
            <div class="form-group">
              <ToggleInput
                id="proposal_price_rounding_enabled"
                v-model="inputs.proposal_price_rounding_enabled.binding"
                label="Prijsafronding inschakelen"
                @update:modelValue="handleChange('proposal_price_rounding_enabled')"
              />
            </div>
            <div v-if="inputs.proposal_price_rounding_enabled.binding" class="form-grid">
              <SelectInput
                id="proposal_price_rounding_increment"
                label="Afronden op"
                v-model="inputs.proposal_price_rounding_increment.binding"
                :options="priceRoundingIncrementOptions"
                @update:modelValue="handleChange('proposal_price_rounding_increment')"
              />
              <SelectInput
                id="proposal_price_rounding_mode"
                label="Afrondingsmethode"
                v-model="inputs.proposal_price_rounding_mode.binding"
                :options="priceRoundingModeOptions"
                @update:modelValue="handleChange('proposal_price_rounding_mode')"
              />
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>Standaard offerte instellingen</h3>
            <p class="section-description">
              Deze waarden worden automatisch ingevuld bij het aanmaken van een nieuwe offerte.
            </p>
            <div class="form-grid">
              <NumberInput
                label="Standaard geldigheidsduur (dagen)"
                v-model="inputs.proposal_valid_days.binding"
                @blur="handleBlur('proposal_valid_days')"
                @update:modelValue="handleChange('proposal_valid_days')"
                :min="1"
                :max="365"
              />
              <WysiwygEditor
                label="Standaard klantnotities"
                :modelValue="inputs.proposal_client_notes.binding"
                @update:modelValue="
                  (val) => {
                    inputs.proposal_client_notes.binding = val;
                    handleChange('proposal_client_notes');
                  }
                "
              />
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>Standaard bijlagen</h3>
            <p class="section-description">
              Deze PDF's worden automatisch meegestuurd met elke offerte-e-mail en zijn door de
              klant te downloaden op de offertepagina (bijv. algemene voorwaarden of een brochure).
            </p>
            <ul v-if="defaultProposalAttachments.length" class="attachment-list">
              <li
                v-for="(att, index) in defaultProposalAttachments"
                :key="att.id || att.url"
                class="attachment-list__item"
              >
                <BaseIcon name="file" :size="14" />
                <a :href="att.url" target="_blank" rel="noopener" class="attachment-list__name">
                  {{ att.name }}
                </a>
                <button
                  type="button"
                  class="attachment-list__remove"
                  title="Bijlage verwijderen"
                  @click="removeDefaultAttachment(index)"
                >
                  <BaseIcon name="xmark" :size="14" />
                </button>
              </li>
            </ul>
            <input
              ref="defaultAttachmentInputRef"
              type="file"
              accept="application/pdf,.pdf"
              multiple
              class="attachment-list__input"
              @change="handleDefaultAttachmentUpload"
            />
            <Btn
              text="PDF toevoegen"
              variant="dashed"
              icon="plus"
              :loading="isAttachmentUploading"
              @click="triggerDefaultAttachmentUpload"
            />
          </div>
          <Divider />
          <div class="settings-section">
            <h3>{{ $t('settings.send_proposal_pdf_title') }}</h3>
            <p class="section-description">{{ $t('settings.send_proposal_pdf_help') }}</p>
            <div class="form-group">
              <ToggleInput
                v-model="inputs.send_proposal_pdf.binding"
                :label="$t('settings.send_proposal_pdf')"
                id="send_proposal_pdf"
                @update:modelValue="handleChange('send_proposal_pdf')"
              />
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>Algemene voorwaarden tekst</h3>
            <p class="section-description">
              De disclaimer die onderaan elke offerte verschijnt.
            </p>
            <WysiwygEditor
              :label="inputs.general_terms.label"
              :modelValue="inputs.general_terms.binding"
              @update:modelValue="(val) => {
                inputs.general_terms.binding = val;
                handleChange('general_terms');
              }"
            />
          </div>
          <template v-if="inputs.proposal_workflow.binding === 'measurement'">
            <Divider />
            <div class="settings-section">
              <h3>Algemene voorwaarden tekst voorlopige offerte</h3>
              <p class="section-description">
                De disclaimer die onderaan elke voorlopige offerte verschijnt. Definitieve offertes
                en meerwerk gebruiken de tekst hierboven.
              </p>
              <WysiwygEditor
                :label="inputs.proforma_general_terms.label"
                :modelValue="inputs.proforma_general_terms.binding"
                @update:modelValue="(val) => {
                  inputs.proforma_general_terms.binding = val;
                  handleChange('proforma_general_terms');
                }"
              />
            </div>
          </template>
          <Divider />
          <div class="settings-section">
            <h3>Offerte acceptatie CC</h3>
            <p class="section-description">
              Voeg e-mailadressen toe die een kopie ontvangen wanneer een offerte wordt
              geaccepteerd.
            </p>
            <div class="form-grid">
              <TagInput
                :id="inputs.proposal_acceptance_cc.id"
                :label="inputs.proposal_acceptance_cc.label"
                v-model="inputs.proposal_acceptance_cc.binding"
                :error="inputs.proposal_acceptance_cc.error"
                :rules="inputs.proposal_acceptance_cc.rules"
                placeholder="naam@bedrijf.nl"
                @update:modelValue="handleChange('proposal_acceptance_cc')"
                @validate="onValidate('proposal_acceptance_cc', $event)"
              />
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>Offerte-herinneringen</h3>
            <p class="section-description">
              Herinneringen worden naar de klant gestuurd zolang de offerte nog niet is geaccepteerd
              of afgewezen. Geteld vanaf de verzenddatum.
            </p>
            <div class="form-group">
              <ToggleInput
                id="proposal_reminders_enabled"
                v-model="proposalReminders.enabled"
                label="Offerte-herinneringen inschakelen"
                @update:modelValue="handleProposalRemindersChange"
              />
            </div>
            <div v-if="proposalReminders.enabled" class="reminder-settings">
              <div class="reminder-intervals">
                <label class="reminder-intervals__label">Herinner na (dagen)</label>
                <div
                  v-for="(interval, index) in proposalReminders.intervals"
                  :key="index"
                  class="reminder-intervals__row"
                >
                  <input
                    type="number"
                    class="reminder-intervals__input"
                    min="1"
                    max="365"
                    :value="interval"
                    placeholder="bijv. 7"
                    @input="updateInterval(index, $event.target.value)"
                  />
                  <Btn
                    icon="trash"
                    variant="plain"
                    size="sm"
                    type="button"
                    @click="removeInterval(index)"
                  />
                </div>
                <Btn
                  icon="plus"
                  variant="dashed"
                  size="sm"
                  type="button"
                  text="Interval toevoegen"
                  :disabled="proposalReminders.intervals.length >= 10"
                  @click="addInterval"
                />
                <p v-if="reminderError" class="help-text help-text--error">{{ reminderError }}</p>
                <p v-if="reminderWarning" class="help-text help-text--warning">
                  {{ reminderWarning }}
                </p>
                <p class="help-text">Hele dagen tussen 1 en 365. Maximaal 10 intervallen.</p>
              </div>
              <div class="form-grid">
                <TextInput
                  id="proposal_reminder_subject"
                  label="Eigen onderwerpregel (optioneel)"
                  v-model="proposalReminders.subject"
                  placeholder="Laat leeg voor de standaard onderwerpregel"
                  @update:modelValue="handleProposalRemindersChange"
                />
                <WysiwygEditor
                  label="Eigen introbericht (optioneel)"
                  :modelValue="proposalReminders.message"
                  @update:modelValue="
                    (val) => {
                      proposalReminders.message = val;
                      handleProposalRemindersChange();
                    }
                  "
                />
                <MessageElement>
                  Laat de velden leeg om de standaard tekst te gebruiken. De knop naar de offerte
                  wordt automatisch toegevoegd.
                </MessageElement>
              </div>
            </div>
          </div>
          <Divider />
          <div class="settings-section">
            <h3>Offertenummering</h3>
            <p class="section-description">
              Configureer hoe offertenummers worden gegenereerd en weergegeven.
            </p>
            <div class="form-grid">
              <div class="form-group">
                <ToggleInput
                  v-model="inputs.proposal_numbering_include_year.binding"
                  label="Jaar opnemen in nummer"
                  :id="inputs.proposal_numbering_include_year.id"
                  @update:modelValue="handleChange('proposal_numbering_include_year')"
                />
                <p class="help-text">
                  Als ingeschakeld, worden offertenummers het jaar bevatten (bijv. 2026-0010)
                </p>
              </div>
              <div class="form-group">
                <TextInput
                  label="Prefix (optioneel)"
                  v-model="inputs.proposal_numbering_prefix.binding"
                  @blur="handleBlur('proposal_numbering_prefix')"
                  @update:modelValue="handleChange('proposal_numbering_prefix')"
                  placeholder="bijv. OFF, PROP, of {year}"
                  :maxlength="20"
                />
                <p class="help-text">Optionele prefix, zoals (OFF-)</p>
              </div>
              <div class="form-group">
                <NumberInput
                  label="Nummer padding"
                  v-model="inputs.proposal_numbering_padding.binding"
                  @blur="handleBlur('proposal_numbering_padding')"
                  @update:modelValue="handleChange('proposal_numbering_padding')"
                  :min="1"
                  :max="10"
                />
                <p class="help-text">
                  Aantal cijfers voor het sequentiële nummer (4 = 0001, 3 = 001)
                </p>
              </div>
            </div>
            <div class="proposal-numbering-preview">
              <strong>Voorbeeld:</strong>
              <span class="preview-number">{{ previewProposalNumber }}</span>
            </div>
            <div class="proposal-numbering-actions">
              <Btn
                variant="danger"
                icon="redo"
                @click="openResetNumbersModal"
                :loading="resettingNumbers"
              >
                Nummering resetten
              </Btn>
              <p class="help-text">
                Reset alle offertenummers. Dit kan niet ongedaan worden gemaakt.
              </p>
            </div>
          </div>
          <template v-if="isSuperAdmin">
            <Divider />
            <div class="settings-section">
              <h3>Custom Proposal Templates</h3>
              <p class="section-description">
                Pas de HTML-templates van offertes aan. Gebruik
                <code v-pre>{{ placeholder }}</code>
                syntax voor dynamische waarden.
              </p>
              <div v-if="templatesLoading" class="templates-loading">
                <LoadingSpinner size="sm" />
                <span>Templates laden...</span>
              </div>
              <template v-else>
                <div class="template-field">
                  <label class="template-field__label">Cover Page HTML</label>
                  <p class="help-text">
                    HTML die als voorblad vóór de offerte wordt geplaatst (eigen pagina met page
                    break).
                  </p>
                  <textarea
                    class="template-field__editor"
                    v-model="proposalTemplates.cover_page_html"
                    rows="8"
                    placeholder='<div style="display:flex; align-items:center; justify-content:center; height:100vh;">&#10;  {{company_logo}}&#10;  <h1>{{proposal_title}}</h1>&#10;</div>'
                    spellcheck="false"
                  />
                </div>
                <div class="template-field">
                  <label class="template-field__label">Closing Page HTML</label>
                  <p class="help-text">
                    HTML die als slotpagina ná de offerte wordt geplaatst (eigen pagina met page
                    break).
                  </p>
                  <textarea
                    class="template-field__editor"
                    v-model="proposalTemplates.closing_page_html"
                    rows="8"
                    placeholder='<div class="closing">&#10;  <h2>Bedankt voor uw vertrouwen</h2>&#10;  <p>Neem contact op via {{company_phone}}</p>&#10;</div>'
                    spellcheck="false"
                  />
                </div>
                <div class="template-field">
                  <label class="template-field__label">Proposal HTML</label>
                  <p class="help-text">
                    Vervangt de volledige standaard offerte-HTML. Cover en closing pages worden er
                    omheen gewrapt. Gebruik blok-placeholders zoals
                    <code v-pre>{{ items_table }}</code>
                    ,
                    <code v-pre>{{ company_info }}</code>
                    , etc.
                  </p>
                  <textarea
                    class="template-field__editor"
                    v-model="proposalTemplates.proposal_html"
                    rows="12"
                    placeholder='<div class="custom-header">{{company_info}}</div>&#10;{{items_table}}&#10;{{signature}}'
                    spellcheck="false"
                  />
                </div>
                <div class="template-field">
                  <label class="template-field__label">Custom CSS</label>
                  <p class="help-text">
                    Extra CSS die wordt samengevoegd met de standaard offerte-CSS.
                  </p>
                  <textarea
                    class="template-field__editor template-field__editor--css"
                    v-model="proposalTemplates.custom_css"
                    rows="8"
                    placeholder=".proposal-cover {&#10;  display: flex;&#10;  align-items: center;&#10;  justify-content: center;&#10;  min-height: 90vh;&#10;}"
                    spellcheck="false"
                  />
                </div>
                <div class="template-actions">
                  <Btn
                    text="Templates opslaan"
                    @click="saveProposalTemplates"
                    :loading="templatesSaving"
                    variant="primary"
                  />
                  <Btn
                    text="Reset naar standaard"
                    @click="resetProposalTemplates"
                    variant="danger"
                    :loading="templatesSaving"
                  />
                </div>
                <details class="template-placeholders">
                  <summary>Beschikbare placeholders</summary>
                  <div class="template-placeholders__grid">
                    <div class="template-placeholders__group">
                      <h4>Bedrijfsgegevens</h4>
                      <code v-for="p in companyPlaceholders" :key="p">{{ p }}</code>
                    </div>
                    <div class="template-placeholders__group">
                      <h4>Klantgegevens</h4>
                      <code v-for="p in clientPlaceholders" :key="p">{{ p }}</code>
                    </div>
                    <div class="template-placeholders__group">
                      <h4>Offerte</h4>
                      <code v-for="p in proposalPlaceholders" :key="p">{{ p }}</code>
                    </div>
                    <div class="template-placeholders__group">
                      <h4>HTML-blokken</h4>
                      <code v-for="p in blockPlaceholders" :key="p">{{ p }}</code>
                    </div>
                  </div>
                </details>
              </template>
            </div>
          </template>
        </Tab>
        <Tab v-if="isAdmin" name="api" :title="$t('settings.api_keys.tab')">
          <OrganizationApiKeysSettings :organization-id="organization.id" />
        </Tab>
        <Tab name="planning" :title="$t('settings.planning.tab')">
          <div class="settings-section">
            <h3>{{ $t('settings.planning_work_time.title') }}</h3>
            <p class="section-description">{{ $t('settings.planning_work_time.description') }}</p>
            <div class="organization-settings__row">
              <div class="organization-settings__field">
                <label for="planning-workday-start">
                  {{ $t('settings.planning_work_time.workday_start') }}
                </label>
                <input
                  id="planning-workday-start"
                  v-model="planningWorkTime.workday_start"
                  type="time"
                  @change="handlePlanningWorkTimeChange"
                />
              </div>
              <div class="organization-settings__field">
                <label for="planning-workday-end">
                  {{ $t('settings.planning_work_time.workday_end') }}
                </label>
                <input
                  id="planning-workday-end"
                  v-model="planningWorkTime.workday_end"
                  type="time"
                  @change="handlePlanningWorkTimeChange"
                />
              </div>
            </div>
            <div class="organization-settings__row">
              <NumberInput
                id="planning-break-minutes"
                :label="$t('settings.planning_work_time.break_minutes')"
                v-model="planningWorkTime.break_minutes_between_jobs"
                :min="0"
                :max="120"
                @update:modelValue="handlePlanningWorkTimeChange"
              />
              <SelectInput
                id="planning-timezone"
                :label="$t('settings.planning_work_time.timezone')"
                v-model="planningWorkTime.timezone"
                :options="planningTimezoneOptions"
                @update:modelValue="handlePlanningWorkTimeChange"
              />
            </div>
            <p v-if="planningWorkTimeError" class="form-message form-message--error">
              {{ planningWorkTimeError }}
            </p>
          </div>

          <div class="settings-section">
            <h3>{{ $t('settings.planning_customer.title') }}</h3>
            <p class="section-description">{{ $t('settings.planning_customer.description') }}</p>

            <ToggleInput
              id="planning-order-confirmation"
              v-model="planningCustomer.order_confirmation_enabled"
              :label="$t('settings.planning_customer.order_confirmation.label')"
              @update:modelValue="handlePlanningCustomerChange"
            />
            <p class="section-description">
              {{ $t('settings.planning_customer.order_confirmation.help') }}
            </p>
            <template v-if="planningCustomer.order_confirmation_enabled">
              <TextAreaInput
                id="planning-order-confirmation-intro"
                :label="$t('settings.planning_customer.order_confirmation.intro_label')"
                v-model="planningCustomer.order_confirmation_intro"
                :rows="4"
                @update:modelValue="handlePlanningCustomerChange"
              />
              <p class="section-description">
                {{ $t('settings.planning_customer.order_confirmation.intro_help') }}
              </p>
            </template>

            <ToggleInput
              id="planning-customer-preference"
              v-model="planningCustomer.customer_preference_enabled"
              :label="$t('settings.planning_customer.preference.label')"
              @update:modelValue="handlePlanningCustomerChange"
            />
            <p class="section-description">
              {{ $t('settings.planning_customer.preference.help') }}
            </p>

            <ToggleInput
              id="planning-appointment-confirmation"
              v-model="planningCustomer.appointment_confirmation_enabled"
              :label="$t('settings.planning_customer.handshake.label')"
              @update:modelValue="handlePlanningCustomerChange"
            />
            <p class="section-description">
              {{ $t('settings.planning_customer.handshake.help') }}
            </p>
            <div
              v-if="planningCustomer.appointment_confirmation_enabled"
              class="organization-settings__row"
            >
              <NumberInput
                id="planning-silent-accept-hours"
                :label="$t('settings.planning_customer.handshake.silent_accept_hours')"
                v-model="planningCustomer.appointment_silent_accept_hours"
                :min="0"
                :max="336"
                @update:modelValue="handlePlanningCustomerChange"
              />
            </div>
            <p
              v-if="planningCustomer.appointment_confirmation_enabled"
              class="section-description"
            >
              {{ $t('settings.planning_customer.handshake.silent_accept_help') }}
            </p>
          </div>
        </Tab>
      </Tabs>
      <div class="mt-6">
        <Btn
          text="Save changes"
          @click="saveChanges"
          :disabled="Object.keys(pendingChanges).length === 0"
          :loading="isSaving"
          v-if="activeTab !== 'team' && activeTab !== 'api'"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import Btn from '@/components/partials/Btn.vue';
  import Card from '@/components/partials/Card.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import InviteMemberModal from '@/pages/settings/InviteMemberModal.vue';
  import MemberWorkHoursModal from '@/components/settings/MemberWorkHoursModal.vue';
  import { summarizeMemberHours, hasMemberOverrides } from '@/utils/technicianWorkTime';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import { useUIStore } from '@/store/uiStore';
  import { useUserStore } from '@/store/userStore';
  import { useValidation } from '@/composables/useValidation';
  import { useToastStore } from '@/store/toastStore';
  import { useRoleCheck } from '@/composables/useRoleCheck';
  import OrganizationService from '@/services/organizationService';
  import { crmService } from '@/services/crmService';
  import AvatarElement from '@/components/partials/Avatar.vue';
  import MessageElement from '@/components/partials/Message.vue';
  import FileService from '@/services/fileService';
  import Divider from '@/components/partials/Divider.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  import Tabs from '@/components/partials/Tabs/Tabs.vue';
  import Tab from '@/components/partials/Tabs/Tab.vue';
  // import ColorInput from '@/components/inputs/ColorInput.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import FileInput from '@/components/inputs/FileInput.vue';
  import ColorPicker from '@/components/inputs/ColorPicker.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import RadioGroup from '@/components/inputs/RadioGroup.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import WysiwygEditor from '@/components/inputs/WysiwygEditor.vue';
  import {
    DEFAULT_TERMS_HTML,
    isBlankRichText,
    selectProposalTermsHtml,
  } from '@shared/utils/proposalTerms';
  import TagInput from '@/components/inputs/TagInput.vue';
  import OrganizationApiKeysSettings from '@/components/settings/OrganizationApiKeysSettings.vue';
  import SenderSignaturePreview from '@/components/crm/SenderSignaturePreview.vue';
  import { stripClosingOrganizationName } from '@/utils/senderSignature';
  import {
    getPlanningWorkTime,
    DEFAULT_WORK_TIME,
    getPlanningCustomerSettings,
  } from '@/constants/planningConstants';
  import { DEFAULT_PRICE_ROUNDING_INCREMENT } from '@shared/utils/moneyUtils';

  const DEFAULT_APPOINTMENT_EMAIL_NOTICE =
    '<p>De aankomsttijd is een indicatie. De monteurs bellen altijd wanneer ze naar uw adres vertrekken.</p>' +
    '<p><strong>Let op:</strong> zorg dat u uw betaallimiet heeft verhoogd voor de afrekening na de montage.</p>';

  export default {
    name: 'SettingsOrganizationView',

    components: {
      PageHeader,
      Btn,
      Card,
      LoadingSpinner,
      AvatarElement,
      EmptyState,
      Tabs,
      Tab,
      TextInput,
      FileInput,
      SelectInput,
      RadioGroup,
      ColorPicker,
      ToggleInput,
      NumberInput,
      TextAreaInput,
      WysiwygEditor,
      MessageElement,
      Divider,
      TagInput,
      BaseIcon,
      OrganizationApiKeysSettings,
      SenderSignaturePreview,
    },

    setup() {
      const route = useRoute();
      const activeTab = ref('general');
      const organizationTabNames = [
        'general',
        'team',
        'brand',
        'crm',
        'proposals',
        'planning',
      ];

      const applyTabFromQuery = () => {
        const tab = route.query.tab;
        if (typeof tab === 'string' && organizationTabNames.includes(tab)) {
          activeTab.value = tab;
        }
      };
      const userStore = useUserStore();
      const organization = computed(() => userStore.currentOrganization);
      const toastStore = useToastStore();
      const pendingChanges = ref({});
      const isSaving = ref(false);

      const workflowOptions = [
        { value: 'direct', label: 'Direct' },
        { value: 'measurement', label: 'Met inmeting' },
      ];
      const priceRoundingIncrementOptions = [
        { value: '0.05', label: '€ 0,05' },
        { value: '0.1', label: '€ 0,10' },
        { value: '0.5', label: '€ 0,50' },
        { value: '1', label: '€ 1,00' },
        { value: '5', label: '€ 5,00' },
        { value: '10', label: '€ 10,00' },
      ];
      const priceRoundingModeOptions = [
        { value: 'nearest', label: 'Dichtstbijzijnde' },
        { value: 'up', label: 'Naar boven' },
        { value: 'down', label: 'Naar beneden' },
      ];
      const isLogoUploading = ref(false);
      const resettingNumbers = ref(false);
      const { inputs, validateForm, onValidate } = useValidation({
        // General Settings
        name: {
          id: 'name',
          label: 'Organization name',
          binding: organization.value.name,
          error: '',
          rules: ['required'],
        },
        language: {
          id: 'language',
          label: 'Language',
          binding: organization.value.language || 'en-US',
          error: '',
          rules: ['required'],
        },
        street_address: {
          id: 'street_address',
          label: 'Street address',
          binding: organization.value.address.street_address,
          error: '',
          rules: [],
        },
        city: {
          id: 'city',
          label: 'City',
          binding: organization.value.address.city,
          error: '',
          rules: [],
        },
        state: {
          id: 'state',
          label: 'State/Province',
          binding: organization.value.address.state,
          error: '',
          rules: [],
        },
        postal_code: {
          id: 'postal_code',
          label: 'Postal code',
          binding: organization.value.address.postal_code,
          error: '',
          rules: [],
        },
        country: {
          id: 'country',
          label: 'Country',
          binding: organization.value.address.country || 'NL',
          error: '',
          rules: [],
        },
        phone: {
          id: 'phone',
          label: 'Phone',
          binding: organization.value.address.phone,
          error: '',
          rules: [],
        },
        vat_number: {
          id: 'vat_number',
          label: 'VAT number',
          binding: organization.value.address.vat_number,
          error: '',
          rules: [],
        },
        registration_number: {
          id: 'registration_number',
          label: 'Registration number',
          binding: organization.value.address.registration_number,
          error: '',
          rules: [],
        },
        bank_account: {
          id: 'bank_account',
          label: 'IBAN',
          binding: organization.value.bank_account || '',
          error: '',
          rules: [],
        },
        bic: {
          id: 'bic',
          label: 'BIC',
          binding: organization.value.bic || '',
          error: '',
          rules: [],
        },

        // Brand Settings
        primary_color: {
          id: 'primary_color',
          label: 'Primary Color',
          binding: organization.value.brand_settings.primary_color,
          error: '',
          rules: ['required'],
        },
        primary_accent_color: {
          id: 'primary_accent_color',
          label: 'Primary accent color',
          binding: organization.value.brand_settings.primary_accent_color,
          error: '',
          rules: ['required'],
        },
        logo: {
          id: 'logo',
          label: 'Logo',
          binding: organization.value.brand_settings.logo,
          error: '',
          rules: ['required'],
        },
        icon: {
          id: 'icon',
          label: 'Icon',
          binding: organization.value.brand_settings.icon,
          error: '',
          rules: [],
        },

        // CRM Settings
        reply_to_email: {
          id: 'reply_to_email',
          label: 'Reply-to email address',
          binding: organization.value.crm_settings?.reply_to_email || '',
          error: '',
          rules: ['email'],
        },
        deal_email_enabled: {
          id: 'deal_email_enabled',
          label: 'CRM email',
          binding: organization.value.crm_settings?.deal_email_enabled === true,
          error: '',
          rules: [],
        },
        show_sender_contact: {
          id: 'show_sender_contact',
          label: 'Show sender contact',
          binding: organization.value.crm_settings?.show_sender_contact === true,
          error: '',
          rules: [],
        },
        proposal_mail_subject: {
          id: 'proposal_mail_subject',
          label: 'Default proposal mail subject',
          binding: organization.value.crm_settings.proposal_mail_subject,
          error: '',
          rules: ['required'],
        },
        proposal_mail_body: {
          id: 'proposal_mail_body',
          label: 'Default proposal mail body',
          binding: stripClosingOrganizationName(
            organization.value.crm_settings.proposal_mail_body,
            organization.value.name,
          ),
          error: '',
          rules: ['required'],
        },
        proposal_introduction: {
          id: 'proposal_introduction',
          label: 'Default proposal introduction',
          binding: organization.value.crm_settings.proposal_introduction,
          error: '',
          rules: ['required'],
        },
        whatsapp_proposal_message: {
          id: 'whatsapp_proposal_message',
          label: 'WhatsApp berichttemplate',
          binding:
            organization.value.crm_settings?.whatsapp_proposal_message ||
            'Beste [lead_first_name],\n\nHierbij ontvangt u de offerte van [organization_name]. Klik op onderstaande link om de offerte te bekijken.\n\nMet vriendelijke groet,\n[organization_name]\n\n[proposal_link]',
          error: '',
          rules: [],
        },
        appointment_email_notice: {
          id: 'appointment_email_notice',
          label: 'Tekst in afspraakbevestiging',
          binding:
            organization.value.crm_settings?.appointment_email_notice ||
            DEFAULT_APPOINTMENT_EMAIL_NOTICE,
          error: '',
          rules: [],
        },
        terms_url: {
          id: 'terms_url',
          label: 'Terms URL',
          binding: organization.value.settings?.terms_url || '',
          error: '',
          rules: ['url'],
        },
        general_terms: {
          id: 'general_terms',
          label: 'Tekst algemene voorwaarden',
          binding:
            selectProposalTermsHtml(organization.value.settings, 'definitive') ||
            DEFAULT_TERMS_HTML,
          error: '',
          rules: [],
        },
        proforma_general_terms: {
          id: 'proforma_general_terms',
          label: 'Tekst algemene voorwaarden voorlopige offerte',
          // Until set, a proforma renders the general terms, so show those.
          binding:
            selectProposalTermsHtml(organization.value.settings, 'proforma') ||
            DEFAULT_TERMS_HTML,
          error: '',
          rules: [],
        },

        // Proposal Defaults
        proposal_valid_days: {
          id: 'proposal_valid_days',
          label: 'Default validity days',
          binding: organization.value.proposal_defaults?.valid_days || 30,
          error: '',
          rules: [],
        },
        proposal_client_notes: {
          id: 'proposal_client_notes',
          label: 'Default client notes',
          binding: organization.value.proposal_defaults?.client_notes || '',
          error: '',
          rules: [],
        },
        // VAT-basis (margin & VAT phase 2). Missing key → default to true
        // (incl. BTW) so existing organisations keep their current
        // consumer-style pricing.
        proposal_prices_include_vat: {
          id: 'proposal_prices_include_vat',
          label: 'Prices include VAT',
          binding: organization.value.proposal_defaults?.prices_include_vat ?? true,
          error: '',
          rules: [],
        },
        // Proposal PRESENTATION basis — separate from the catalog storage
        // basis above. Missing key → 'incl' (B2C default).
        proposal_display_basis: {
          id: 'proposal_display_basis',
          label: 'Proposal display basis',
          binding: organization.value.proposal_defaults?.proposal_display_basis || 'incl',
          error: '',
          rules: [],
        },
        proposal_price_visibility: {
          id: 'proposal_price_visibility',
          label: 'Proposal price visibility',
          binding: organization.value.proposal_defaults?.price_visibility || 'line_items',
          error: '',
          rules: [],
        },
        proposal_price_rounding_enabled: {
          id: 'proposal_price_rounding_enabled',
          label: 'Price rounding enabled',
          binding: organization.value.proposal_defaults?.price_rounding?.enabled === true,
          error: '',
          rules: [],
        },
        proposal_price_rounding_increment: {
          id: 'proposal_price_rounding_increment',
          label: 'Price rounding increment',
          binding: String(
            organization.value.proposal_defaults?.price_rounding?.increment ??
              DEFAULT_PRICE_ROUNDING_INCREMENT
          ),
          error: '',
          rules: [],
        },
        proposal_price_rounding_mode: {
          id: 'proposal_price_rounding_mode',
          label: 'Price rounding mode',
          binding: organization.value.proposal_defaults?.price_rounding?.mode || 'nearest',
          error: '',
          rules: [],
        },
        proposal_acceptance_cc: {
          id: 'proposal_acceptance_cc',
          label: 'CC ontvangers (druk op enter om te scheiden)',
          binding: Array.isArray(organization.value.settings?.proposal_acceptance_cc)
            ? organization.value.settings.proposal_acceptance_cc.join(', ')
            : '',
          error: '',
          rules: [],
        },

        // Proposal Workflow (pro forma flow)
        proposal_workflow: {
          id: 'proposal_workflow',
          label: 'Proposal workflow',
          binding: organization.value.settings?.proposal_workflow || 'direct',
          error: '',
          rules: [],
        },
        auto_accept_definitive: {
          id: 'auto_accept_definitive',
          label: 'Auto-accept definitive',
          binding: organization.value.settings?.auto_accept_definitive || false,
          error: '',
          rules: [],
        },
        send_proposal_pdf: {
          id: 'send_proposal_pdf',
          label: 'Send proposal PDF',
          // Default the UI to "on" when unset; we only write the key on change
          // (trackChange), so untouched orgs stay null and keep attaching the PDF.
          binding: organization.value.settings?.send_proposal_pdf ?? true,
          error: '',
          rules: [],
        },
        auto_accept_max_increase: {
          id: 'auto_accept_max_increase',
          label: 'Max price increase %',
          binding: organization.value.settings?.auto_accept_max_increase ?? 5,
          error: '',
          rules: [],
        },
        // Empty = legacy pair (commercial cap + auto-accept) until a mode is picked.
        price_deviation_mode: {
          id: 'price_deviation_mode',
          label: 'Price deviation mode',
          binding: organization.value.settings?.price_deviation_mode || '',
          error: '',
          rules: [],
        },
        price_deviation_max_percent: {
          id: 'price_deviation_max_percent',
          label: 'Price deviation max %',
          binding:
            organization.value.settings?.price_deviation_max_percent ??
            organization.value.settings?.auto_accept_max_increase ??
            5,
          error: '',
          rules: [],
        },
        // Proposal Numbering Settings
        proposal_numbering_prefix: {
          id: 'proposal_numbering_prefix',
          label: 'Prefix',
          binding: organization.value.settings?.proposal_numbering?.prefix || '',
          error: '',
          rules: [],
        },
        proposal_numbering_include_year: {
          id: 'proposal_numbering_include_year',
          label: 'Include year in number',
          binding: organization.value.settings?.proposal_numbering?.include_year !== false,
          error: '',
          rules: [],
        },
        proposal_numbering_padding: {
          id: 'proposal_numbering_padding',
          label: 'Number padding',
          binding: organization.value.settings?.proposal_numbering?.number_padding || 4,
          error: '',
          rules: [],
        },
      });

      // Proposal reminders (organizations.settings.proposal_reminders).
      // Kept outside `inputs` because `intervals` is a dynamic list and the
      // backend replaces the whole proposal_reminders object on each PATCH,
      // so we always send the complete object.
      const proposalReminders = reactive({
        enabled: organization.value.settings?.proposal_reminders?.enabled ?? false,
        intervals: Array.isArray(organization.value.settings?.proposal_reminders?.intervals)
          ? [...organization.value.settings.proposal_reminders.intervals]
          : [7, 14, 21],
        subject: organization.value.settings?.proposal_reminders?.subject || '',
        message: organization.value.settings?.proposal_reminders?.message || '',
      });
      const reminderError = ref('');

      const planningTimezoneOptions = [
        { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam' },
        { value: 'Europe/Brussels', label: 'Europe/Brussels' },
        { value: 'Europe/Berlin', label: 'Europe/Berlin' },
        { value: 'Europe/London', label: 'Europe/London' },
        { value: 'Europe/Paris', label: 'Europe/Paris' },
      ];
      const initialPlanningWorkTime = getPlanningWorkTime(organization.value);
      const planningWorkTime = reactive({
        workday_start: initialPlanningWorkTime.workday_start,
        workday_end: initialPlanningWorkTime.workday_end,
        break_minutes_between_jobs: initialPlanningWorkTime.break_minutes_between_jobs,
        timezone: initialPlanningWorkTime.timezone,
      });
      const planningWorkTimeError = ref('');

      const validatePlanningWorkTime = () => {
        if (!planningWorkTime.workday_start || !planningWorkTime.workday_end) {
          planningWorkTimeError.value = 'Vul start- en eindtijd in.';
          return false;
        }
        if (planningWorkTime.workday_start >= planningWorkTime.workday_end) {
          planningWorkTimeError.value = 'Starttijd moet vóór eindtijd liggen.';
          return false;
        }
        const breakMins = Number(planningWorkTime.break_minutes_between_jobs);
        if (!Number.isFinite(breakMins) || breakMins < 0 || breakMins > 120) {
          planningWorkTimeError.value = 'Pauze moet tussen 0 en 120 minuten zijn.';
          return false;
        }
        planningWorkTimeError.value = '';
        return true;
      };

      const buildPlanningWorkTimePayload = () => ({
        workday_start: planningWorkTime.workday_start,
        workday_end: planningWorkTime.workday_end,
        break_minutes_between_jobs: Number(planningWorkTime.break_minutes_between_jobs) || 0,
        timezone: planningWorkTime.timezone || DEFAULT_WORK_TIME.timezone,
      });

      const handlePlanningWorkTimeChange = () => {
        validatePlanningWorkTime();
        pendingChanges.value.settings = {
          ...(pendingChanges.value.settings || {}),
          planning_work_time: buildPlanningWorkTimePayload(),
        };
      };

      // Customer-facing planning communication (order confirmation, preference
      // page, appointment handshake). Stored as settings.planning; backend merges.
      const planningCustomer = reactive(getPlanningCustomerSettings(organization.value));

      const handlePlanningCustomerChange = () => {
        const hours = Number(planningCustomer.appointment_silent_accept_hours);
        pendingChanges.value.settings = {
          ...(pendingChanges.value.settings || {}),
          planning: {
            order_confirmation_enabled: planningCustomer.order_confirmation_enabled === true,
            order_confirmation_intro: planningCustomer.order_confirmation_intro || '',
            customer_preference_enabled: planningCustomer.customer_preference_enabled === true,
            appointment_confirmation_enabled:
              planningCustomer.appointment_confirmation_enabled === true,
            appointment_silent_accept_hours: Number.isFinite(hours)
              ? Math.min(336, Math.max(0, Math.round(hours)))
              : 48,
          },
        };
      };

      watch(
        () => organization.value?.id,
        () => {
          const wt = getPlanningWorkTime(organization.value);
          planningWorkTime.workday_start = wt.workday_start;
          planningWorkTime.workday_end = wt.workday_end;
          planningWorkTime.break_minutes_between_jobs = wt.break_minutes_between_jobs;
          planningWorkTime.timezone = wt.timezone;
          planningWorkTimeError.value = '';
          Object.assign(planningCustomer, getPlanningCustomerSettings(organization.value));
        }
      );

      // Non-blocking warning: enabling reminders without any intervals means no
      // emails will ever be sent. The backend allows this, so we warn instead
      // of blocking the save.
      const reminderWarning = computed(() => {
        if (!proposalReminders.enabled) return '';
        const validIntervals = proposalReminders.intervals.filter((n) =>
          Number.isInteger(parseInt(n, 10))
        );
        if (validIntervals.length === 0) {
          return 'Herinneringen zijn ingeschakeld, maar er zijn geen intervallen ingesteld — er worden geen herinneringen verstuurd.';
        }
        return '';
      });

      const validateReminders = () => {
        if (!proposalReminders.enabled) {
          reminderError.value = '';
          return true;
        }
        const nums = proposalReminders.intervals.map((n) => parseInt(n, 10));
        if (nums.some((n) => !Number.isInteger(n) || n < 1 || n > 365)) {
          reminderError.value = 'Gebruik hele dagen tussen 1 en 365.';
          return false;
        }
        if (proposalReminders.intervals.length > 10) {
          reminderError.value = 'Maximaal 10 intervallen toegestaan.';
          return false;
        }
        if (new Set(nums).size !== nums.length) {
          reminderError.value = 'Verwijder dubbele intervallen.';
          return false;
        }
        reminderError.value = '';
        return true;
      };

      const buildReminderPayload = () => {
        const intervals = [
          ...new Set(
            proposalReminders.intervals
              .map((n) => parseInt(n, 10))
              .filter((n) => Number.isInteger(n) && n >= 1 && n <= 365)
          ),
        ].sort((a, b) => a - b);
        const payload = {
          enabled: !!proposalReminders.enabled,
          intervals,
        };
        if (proposalReminders.subject && proposalReminders.subject.trim()) {
          payload.subject = proposalReminders.subject.trim();
        }
        if (proposalReminders.message && proposalReminders.message.trim()) {
          payload.message = proposalReminders.message;
        }
        return payload;
      };

      const handleProposalRemindersChange = () => {
        validateReminders();
        pendingChanges.value.settings = {
          ...(pendingChanges.value.settings || {}),
          proposal_reminders: buildReminderPayload(),
        };
      };

      const addInterval = () => {
        if (proposalReminders.intervals.length >= 10) return;
        proposalReminders.intervals.push(null);
        handleProposalRemindersChange();
      };

      const updateInterval = (index, value) => {
        proposalReminders.intervals[index] =
          value === '' || value === null ? null : parseInt(value, 10);
        handleProposalRemindersChange();
      };

      const removeInterval = (index) => {
        proposalReminders.intervals.splice(index, 1);
        handleProposalRemindersChange();
      };

      const trackChange = (fieldName) => {
        // Safety check
        if (!inputs.value[fieldName]) {
          console.warn(`Field ${fieldName} not found in inputs`);
          return;
        }

        const newValue = inputs.value[fieldName].binding;
        // Track the change but don't save immediately
        if (
          [
            'street_address',
            'city',
            'state',
            'postal_code',
            'country',
            'phone',
            'vat_number',
            'registration_number',
          ].includes(fieldName)
        ) {
          pendingChanges.value.address = {
            ...(pendingChanges.value.address || {}),
            [fieldName]: newValue,
          };
        } else if (['primary_color', 'primary_accent_color', 'logo', 'icon'].includes(fieldName)) {
          pendingChanges.value.brand_settings = {
            ...(pendingChanges.value.brand_settings || {}),
            [fieldName]: newValue,
          };
        } else if (
          [
            'reply_to_email',
            'deal_email_enabled',
            'show_sender_contact',
            'proposal_mail_subject',
            'proposal_mail_body',
            'proposal_introduction',
            'whatsapp_proposal_message',
            'appointment_email_notice',
          ].includes(fieldName)
        ) {
          pendingChanges.value.crm_settings = {
            ...(pendingChanges.value.crm_settings || {}),
            [fieldName]: newValue,
          };
        } else if (fieldName === 'terms_url') {
          pendingChanges.value.settings = {
            ...(pendingChanges.value.settings || {}),
            terms_url: newValue,
          };
        } else if (fieldName === 'general_terms') {
          pendingChanges.value.settings = {
            ...(pendingChanges.value.settings || {}),
            general_terms: isBlankRichText(newValue) ? null : newValue,
          };
        } else if (fieldName === 'proforma_general_terms') {
          pendingChanges.value.settings = {
            ...(pendingChanges.value.settings || {}),
            proforma_general_terms: isBlankRichText(newValue) ? null : newValue,
          };
        } else if (fieldName === 'proposal_acceptance_cc') {
          const recipients = (newValue || '')
            .split(',')
            .map((email) => email.trim())
            .filter((email) => email);
          pendingChanges.value.settings = {
            ...(pendingChanges.value.settings || {}),
            proposal_acceptance_cc: recipients,
          };
        } else if (
          [
            'proposal_valid_days',
            'proposal_client_notes',
            'proposal_prices_include_vat',
            'proposal_display_basis',
            'proposal_price_visibility',
          ].includes(fieldName)
        ) {
          // Backend merges within proposal_defaults (2026-05-12 release), so
          // we only need to send the keys that changed — `valid_days` and
          // `client_notes` are preserved automatically when only updating
          // `prices_include_vat` and vice versa.
          const keyMap = {
            proposal_valid_days: 'valid_days',
            proposal_client_notes: 'client_notes',
            proposal_prices_include_vat: 'prices_include_vat',
            proposal_display_basis: 'proposal_display_basis',
            proposal_price_visibility: 'price_visibility',
          };
          pendingChanges.value.proposal_defaults = {
            ...(pendingChanges.value.proposal_defaults || {}),
            [keyMap[fieldName]]:
              fieldName === 'proposal_valid_days' ? Number(newValue) : newValue,
          };
          if (fieldName === 'proposal_prices_include_vat') {
            pendingChanges.value.proposal_defaults.display_basis = newValue ? 'incl' : 'excl';
          }
        } else if (
          [
            'proposal_price_rounding_enabled',
            'proposal_price_rounding_increment',
            'proposal_price_rounding_mode',
          ].includes(fieldName)
        ) {
          // Always persist the values currently shown in the UI. Enabling the
          // toggle used to write `{ enabled: true }` only, while the increment
          // dropdown already displayed €1 — that value was never saved unless
          // the user changed the dropdown.
          const incrementRaw =
            fieldName === 'proposal_price_rounding_increment'
              ? newValue
              : inputs.value.proposal_price_rounding_increment.binding;
          let increment = Number(incrementRaw);
          if (!Number.isFinite(increment) || increment <= 0) {
            increment = DEFAULT_PRICE_ROUNDING_INCREMENT;
          }
          pendingChanges.value.proposal_defaults = {
            ...(pendingChanges.value.proposal_defaults || {}),
            price_rounding: {
              scope: 'configurator',
              enabled:
                fieldName === 'proposal_price_rounding_enabled'
                  ? newValue === true
                  : inputs.value.proposal_price_rounding_enabled.binding === true,
              increment,
              mode:
                fieldName === 'proposal_price_rounding_mode'
                  ? newValue
                  : inputs.value.proposal_price_rounding_mode.binding || 'nearest',
            },
          };
        } else if (
          [
            'proposal_numbering_prefix',
            'proposal_numbering_include_year',
            'proposal_numbering_padding',
          ].includes(fieldName)
        ) {
          const keyMap = {
            proposal_numbering_prefix: 'prefix',
            proposal_numbering_include_year: 'include_year',
            proposal_numbering_padding: 'number_padding',
          };
          pendingChanges.value.settings = {
            ...(pendingChanges.value.settings || {}),
            proposal_numbering: {
              ...(pendingChanges.value.settings?.proposal_numbering ||
                organization.value.settings?.proposal_numbering ||
                {}),
              [keyMap[fieldName]]: newValue,
            },
          };
        } else if (
          [
            'proposal_workflow',
            'auto_accept_definitive',
            'auto_accept_max_increase',
            'send_proposal_pdf',
          ].includes(fieldName)
        ) {
          pendingChanges.value.settings = {
            ...(pendingChanges.value.settings || {}),
            [fieldName]: newValue,
          };
        } else if (['price_deviation_mode', 'price_deviation_max_percent'].includes(fieldName)) {
          // Saved as a pair: picking a mode must also persist the margin shown.
          pendingChanges.value.settings = {
            ...(pendingChanges.value.settings || {}),
            price_deviation_mode: inputs.value.price_deviation_mode.binding || null,
            price_deviation_max_percent: Number(inputs.value.price_deviation_max_percent.binding),
          };
        } else {
          pendingChanges.value[fieldName] = newValue;
        }
      };

      const saveChanges = async () => {
        if (!validateReminders()) {
          toastStore.addToast({
            message: reminderError.value || 'Controleer de offerte-herinneringen',
            type: 'error',
          });
          return;
        }
        if (!validatePlanningWorkTime()) {
          toastStore.addToast({
            message: planningWorkTimeError.value || 'Controleer de planning werktijden',
            type: 'error',
          });
          return;
        }
        isSaving.value = true;
        if (Object.keys(pendingChanges.value).length === 0) return;
        try {
          await userStore.updateOrganization(organization.value.id, pendingChanges.value);
          toastStore.addToast({
            message: 'Settings updated successfully',
            type: 'success',
          });
          // Clear pending changes after successful save
          pendingChanges.value = {};
        } catch (error) {
          console.error('Failed to update organization:', error);
          toastStore.addToast({
            message: error.response?.data?.error || 'Failed to update settings',
            type: 'error',
          });
        } finally {
          isSaving.value = false;
        }
      };

      // Use this for blur events (immediate save)
      const handleBlur = (fieldName) => {
        trackChange(fieldName);
      };

      // Use this for change events (debounced save)
      const handleChange = (fieldName) => {
        trackChange(fieldName);
      };

      // Radio buttons emit a `change` event but no v-model — we set the
      // binding manually so we can use plain native inputs (the existing
      // `RadioGroup` component only supports String values).
      const handleVatBasisChange = (value) => {
        inputs.value.proposal_prices_include_vat.binding = value;
        trackChange('proposal_prices_include_vat');
      };

      const handleProposalBasisChange = (basis) => {
        inputs.value.proposal_display_basis.binding = basis;
        trackChange('proposal_display_basis');
      };

      const handlePriceVisibilityChange = (value) => {
        inputs.value.proposal_price_visibility.binding = value;
        trackChange('proposal_price_visibility');
      };

      const handleLogoUpload = async (file, type = 'logo') => {
        if (!file) return;
        isLogoUploading.value = true;
        try {
          const response = await FileService.uploadFile(file, {
            type: type,
            organizationId: organization.value.id,
            context: 'organization',
          });

          if (response?.url) {
            pendingChanges.value.brand_settings = {
              ...(pendingChanges.value.brand_settings || {}),
              type: response.url,
            };
            inputs.value[type].binding = response.url;
          }
        } catch (error) {
          console.error('Failed to upload logo:', error);
          toastStore.addToast({
            message: 'Failed to upload logo',
            type: 'error',
          });
        } finally {
          isLogoUploading.value = false;
        }
      };

      // --- Default proposal attachments (settings.default_proposal_attachments) ---
      const defaultProposalAttachments = ref(
        Array.isArray(organization.value?.settings?.default_proposal_attachments)
          ? [...organization.value.settings.default_proposal_attachments]
          : []
      );
      const defaultAttachmentInputRef = ref(null);
      const isAttachmentUploading = ref(false);
      const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;

      // Saved immediately (not via pendingChanges/save button): an upload is
      // an explicit action and shouldn't silently get lost when the user
      // navigates away without pressing save.
      const saveDefaultAttachments = async () => {
        await userStore.updateOrganization(organization.value.id, {
          settings: {
            default_proposal_attachments: defaultProposalAttachments.value,
          },
        });
      };

      const triggerDefaultAttachmentUpload = () => {
        defaultAttachmentInputRef.value?.click();
      };

      const handleDefaultAttachmentUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;

        isAttachmentUploading.value = true;
        let uploadedCount = 0;
        try {
          for (const file of files) {
            const isPdf =
              file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            if (!isPdf) {
              toastStore.addToast({
                message: 'Alleen PDF-bestanden zijn toegestaan',
                type: 'error',
              });
              continue;
            }
            if (file.size > MAX_ATTACHMENT_SIZE) {
              toastStore.addToast({
                message: `${file.name} is te groot (maximaal 5MB)`,
                type: 'error',
              });
              continue;
            }

            const uploaded = await FileService.uploadFile(file, {
              type: 'proposal_attachment',
              organizationId: organization.value.id,
              context: 'organization',
            });

            defaultProposalAttachments.value.push({
              id: uploaded.id,
              name: uploaded.original_name || file.name,
              url: uploaded.url,
              file_name: uploaded.file_name,
              size: uploaded.size ?? file.size,
            });
            uploadedCount += 1;
          }

          if (uploadedCount > 0) {
            await saveDefaultAttachments();
            toastStore.addToast({
              message:
                uploadedCount === 1 ? 'Bijlage toegevoegd' : `${uploadedCount} bijlagen toegevoegd`,
              type: 'success',
            });
          }
        } catch (error) {
          console.error('Failed to upload default proposal attachment:', error);
          toastStore.addToast({
            message: 'Opslaan van bijlage mislukt',
            type: 'error',
          });
        } finally {
          isAttachmentUploading.value = false;
        }
      };

      const removeDefaultAttachment = async (index) => {
        const removed = defaultProposalAttachments.value.splice(index, 1);
        try {
          await saveDefaultAttachments();
          toastStore.addToast({
            message: 'Bijlage verwijderd',
            type: 'success',
          });
        } catch (error) {
          console.error('Failed to remove default proposal attachment:', error);
          // Restore the item so the UI keeps matching what is stored
          defaultProposalAttachments.value.splice(index, 0, ...removed);
          toastStore.addToast({
            message: 'Verwijderen van bijlage mislukt',
            type: 'error',
          });
        }
      };

      const organizationService = new OrganizationService();
      const uiStore = useUIStore();

      const teamMembers = ref([]);
      const pendingInvites = ref([]);
      const cancellingInviteId = ref(null);
      const resendingInviteId = ref(null);
      const resendCooldowns = ref({}); // Track cooldown timestamps per invite
      const COOLDOWN_DURATION = 30000; // 30 seconds in milliseconds

      const openInviteMemberModal = () => {
        const modalId = uiStore.addModal(InviteMemberModal, {
          title: 'Invite team member',
          description: 'Invite a new team member to your organization',
          size: 'sm',
          organization: organization.value,
          onSubmit: (userData) => {
            toastStore.addToast({
              message: 'User invited successfully',
              type: 'success',
            });
            uiStore.closeModal(modalId);
          },
          onError: (error) => {
            console.error(error);
          },
        });
      };

      const fetchTeamData = async () => {
        try {
          const members = await organizationService.getMembers(organization.value.id);
          const invites = await organizationService.getInvites(organization.value.id);
          console.log('TEAM', members);
          teamMembers.value = Array.isArray(members) ? members : members?.data || [];
          pendingInvites.value = Array.isArray(invites) ? invites : invites?.data || [];
        } catch (error) {
          console.error('Failed to fetch team data:', error);
        }
      };

      const getRoleLabel = (role) => {
        const labels = {
          super_admin: 'Super Admin',
          admin: 'Admin',
          member: 'Medewerker',
          installer: 'Monteur',
        };
        return labels[role] || role;
      };

      // Roles that can be assigned via the member list. super_admin is
      // intentionally excluded — that role can never be granted from here.
      const assignableRoles = [
        { value: 'admin', label: 'Admin' },
        { value: 'member', label: 'Medewerker' },
        { value: 'installer', label: 'Monteur' },
      ];
      const updatingMemberRoleId = ref(null);

      // Own row stays read-only so an admin can't accidentally demote
      // themselves and lose access to this page.
      const isCurrentUser = (member) => {
        const memberUserId = member.user?.id ?? member.user_id;
        const currentUserId = userStore.userData?.id || userStore.user?.id;
        return !!memberUserId && memberUserId === currentUserId;
      };

      const handleMemberRoleChange = async (member, newRole) => {
        if (newRole === member.role) return;
        const previousRole = member.role;
        member.role = newRole;
        updatingMemberRoleId.value = member.id;
        try {
          await organizationService.updateMemberRole(organization.value.id, member.id, newRole);
          toastStore.addToast({
            message: 'Rol bijgewerkt',
            type: 'success',
          });
        } catch (error) {
          console.error('Failed to update member role:', error);
          member.role = previousRole;
          toastStore.addToast({
            message: error.response?.data?.error || 'Rol bijwerken mislukt',
            type: 'error',
          });
        } finally {
          updatingMemberRoleId.value = null;
        }
      };

      const openMemberWorkHoursModal = (member) => {
        const modalId = uiStore.addModal(MemberWorkHoursModal, {
          title: 'Werktijden',
          size: 'md',
          member,
          orgWorkTime: { ...planningWorkTime },
          onSubmit: async (planningSettings) => {
            try {
              const updated = await organizationService.updateMemberPlanningSettings(
                organization.value.id,
                member.id,
                planningSettings
              );
              member.planning_settings = updated?.planning_settings ?? planningSettings;
              uiStore.closeModal(modalId);
              toastStore.addToast({ message: 'Werktijden opgeslagen', type: 'success' });
            } catch (error) {
              console.error('Failed to update member work hours:', error);
              toastStore.addToast({
                message: error.response?.data?.error || 'Werktijden opslaan mislukt',
                type: 'error',
              });
            }
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      const cancelInvite = async (inviteId) => {
        cancellingInviteId.value = inviteId;
        try {
          await organizationService.cancelInvite(inviteId);
          // Remove from local list
          pendingInvites.value = pendingInvites.value.filter((invite) => invite.id !== inviteId);
          toastStore.addToast({
            message: 'Invite cancelled successfully',
            type: 'success',
          });
        } catch (error) {
          console.error('Failed to cancel invite:', error);
          toastStore.addToast({
            message: 'Failed to cancel invite',
            type: 'error',
          });
        } finally {
          cancellingInviteId.value = null;
        }
      };

      const resendInvite = async (inviteId) => {
        if (isResendDisabled(inviteId)) return;

        resendingInviteId.value = inviteId;
        try {
          await organizationService.resendInvite(inviteId);
          // Set cooldown timestamp
          resendCooldowns.value[inviteId] = Date.now();

          toastStore.addToast({
            message: 'Invite resent successfully',
            type: 'success',
          });

          // Clear cooldown after duration
          setTimeout(() => {
            delete resendCooldowns.value[inviteId];
          }, COOLDOWN_DURATION);
        } catch (error) {
          console.error('Failed to resend invite:', error);
          toastStore.addToast({
            message: 'Failed to resend invite',
            type: 'error',
          });
        } finally {
          resendingInviteId.value = null;
        }
      };

      const isResendDisabled = (inviteId) => {
        const cooldownTimestamp = resendCooldowns.value[inviteId];
        if (!cooldownTimestamp) return false;

        const timeElapsed = Date.now() - cooldownTimestamp;
        return timeElapsed < COOLDOWN_DURATION;
      };

      const getResendButtonText = (inviteId) => {
        if (isResendDisabled(inviteId)) {
          const cooldownTimestamp = resendCooldowns.value[inviteId];
          const timeElapsed = Date.now() - cooldownTimestamp;
          const secondsRemaining = Math.ceil((COOLDOWN_DURATION - timeElapsed) / 1000);
          return `Resend (${secondsRemaining}s)`;
        }
        return 'Resend';
      };

      // Reset proposal numbers
      const openResetNumbersModal = () => {
        const modalId = uiStore.addModal(null, {
          title: 'Offertenummers resetten',
          description:
            'Weet je zeker dat je alle offertenummers wilt resetten? Deze actie kan niet ongedaan worden gemaakt.',
          status: 'danger',
          submitText: 'Resetten',
          submitVariant: 'danger',
          onSubmit: async () => {
            resettingNumbers.value = true;
            try {
              await crmService.resetProposalNumbers();
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: 'Offertenummers succesvol gereset',
                type: 'success',
                duration: 3000,
              });
            } catch (error) {
              console.error('Error resetting proposal numbers:', error);
              const errorMessage =
                error.response?.data?.error || 'Fout bij het resetten van offertenummers';
              toastStore.addToast({
                message: errorMessage,
                type: 'error',
                duration: 5000,
              });
            } finally {
              resettingNumbers.value = false;
            }
          },
        });
      };

      // Preview for proposal numbering
      const previewProposalNumber = computed(() => {
        const prefix = inputs.value.proposal_numbering_prefix?.binding || '';
        const numberPadding = inputs.value.proposal_numbering_padding?.binding || 4;
        const includeYear = inputs.value.proposal_numbering_include_year?.binding !== false;
        const year = new Date().getFullYear();
        const number = String(1).padStart(numberPadding, '0');

        // Handle {year} in prefix
        let actualPrefix = prefix;
        if (prefix === '{year}') {
          actualPrefix = year.toString();
        }

        // Build format based on prefix and include_year
        if (actualPrefix && includeYear) {
          return `${actualPrefix}-${year}-${number}`;
        } else if (actualPrefix) {
          return `${actualPrefix}-${number}`;
        } else if (includeYear) {
          return `${year}-${number}`;
        } else {
          return number;
        }
      });

      // Proposal templates (super_admin only) + API keys (admin)
      const { isSuperAdmin, isAdmin } = useRoleCheck();
      const proposalTemplates = reactive({
        cover_page_html: '',
        closing_page_html: '',
        proposal_html: '',
        custom_css: '',
      });
      const templatesLoading = ref(false);
      const templatesSaving = ref(false);

      const companyPlaceholders = [
        '{{company_name}}',
        '{{company_logo}}',
        '{{company_logo_url}}',
        '{{company_street}}',
        '{{company_postal_code}}',
        '{{company_city}}',
        '{{company_phone}}',
        '{{company_kvk}}',
        '{{company_iban}}',
      ];
      const clientPlaceholders = [
        '{{client_name}}',
        '{{client_first_name}}',
        '{{client_last_name}}',
        '{{client_company}}',
        '{{client_email}}',
        '{{client_phone}}',
        '{{client_street}}',
        '{{client_house_number}}',
        '{{client_postal_code}}',
        '{{client_city}}',
        '{{client_country}}',
      ];
      const proposalPlaceholders = [
        '{{proposal_title}}',
        '{{proposal_number}}',
        '{{proposal_date}}',
        '{{valid_until}}',
        '{{total_amount}}',
        '{{subtotal}}',
        '{{vat_amount}}',
        '{{client_notes}}',
      ];
      const blockPlaceholders = [
        '{{items_table}}',
        '{{company_info}}',
        '{{client_info}}',
        '{{reference}}',
        '{{notes}}',
        '{{terms}}',
        '{{signature}}',
      ];

      const fetchProposalTemplates = async () => {
        templatesLoading.value = true;
        try {
          const data = await organizationService.getProposalTemplates();
          proposalTemplates.cover_page_html = data.cover_page_html || '';
          proposalTemplates.closing_page_html = data.closing_page_html || '';
          proposalTemplates.proposal_html = data.proposal_html || '';
          proposalTemplates.custom_css = data.custom_css || '';
        } catch (error) {
          console.error('Failed to fetch proposal templates:', error);
        } finally {
          templatesLoading.value = false;
        }
      };

      const saveProposalTemplates = async () => {
        templatesSaving.value = true;
        try {
          const payload = {
            cover_page_html: proposalTemplates.cover_page_html || null,
            closing_page_html: proposalTemplates.closing_page_html || null,
            proposal_html: proposalTemplates.proposal_html || null,
            custom_css: proposalTemplates.custom_css || null,
          };
          await organizationService.updateProposalTemplates(payload);
          toastStore.addToast({
            message: 'Proposal templates opgeslagen',
            type: 'success',
          });
        } catch (error) {
          console.error('Failed to save proposal templates:', error);
          toastStore.addToast({
            message: 'Fout bij het opslaan van templates',
            type: 'error',
          });
        } finally {
          templatesSaving.value = false;
        }
      };

      const resetProposalTemplates = () => {
        const modalId = uiStore.addModal(null, {
          title: 'Templates resetten',
          description:
            'Weet je zeker dat je alle custom templates wilt verwijderen? De standaard offerte-layout wordt weer gebruikt.',
          status: 'danger',
          submitText: 'Resetten',
          submitVariant: 'danger',
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            templatesSaving.value = true;
            try {
              await organizationService.updateProposalTemplates({
                cover_page_html: null,
                closing_page_html: null,
                proposal_html: null,
                custom_css: null,
              });
              proposalTemplates.cover_page_html = '';
              proposalTemplates.closing_page_html = '';
              proposalTemplates.proposal_html = '';
              proposalTemplates.custom_css = '';
              toastStore.addToast({
                message: 'Templates gereset naar standaard',
                type: 'success',
              });
            } catch (error) {
              console.error('Failed to reset templates:', error);
              toastStore.addToast({
                message: 'Fout bij het resetten van templates',
                type: 'error',
              });
            } finally {
              templatesSaving.value = false;
            }
          },
        });
      };

      let countdownInterval = null;

      onMounted(() => {
        applyTabFromQuery();
        fetchTeamData();
        if (isSuperAdmin.value) {
          fetchProposalTemplates();
        }

        countdownInterval = setInterval(() => {
          if (Object.keys(resendCooldowns.value).length > 0) {
            const _ = resendCooldowns.value;
          }
        }, 1000);
      });

      watch(
        () => route.query.tab,
        () => {
          applyTabFromQuery();
        }
      );

      onBeforeUnmount(() => {
        if (countdownInterval) {
          clearInterval(countdownInterval);
        }
      });

      return {
        organization,
        openInviteMemberModal,
        teamMembers,
        pendingInvites,
        activeTab,
        inputs,
        workflowOptions,
        priceRoundingIncrementOptions,
        priceRoundingModeOptions,
        handleBlur,
        handleChange,
        handleVatBasisChange,
        handleProposalBasisChange,
        handlePriceVisibilityChange,
        proposalReminders,
        reminderError,
        reminderWarning,
        handleProposalRemindersChange,
        addInterval,
        updateInterval,
        removeInterval,
        planningWorkTime,
        planningWorkTimeError,
        planningTimezoneOptions,
        handlePlanningWorkTimeChange,
        planningCustomer,
        handlePlanningCustomerChange,
        onValidate,
        pendingChanges,
        saveChanges,
        isSaving,
        handleLogoUpload,
        defaultProposalAttachments,
        defaultAttachmentInputRef,
        isAttachmentUploading,
        triggerDefaultAttachmentUpload,
        handleDefaultAttachmentUpload,
        removeDefaultAttachment,
        getRoleLabel,
        assignableRoles,
        updatingMemberRoleId,
        isCurrentUser,
        handleMemberRoleChange,
        openMemberWorkHoursModal,
        summarizeMemberHours,
        hasMemberOverrides,
        cancelInvite,
        resendInvite,
        cancellingInviteId,
        resendingInviteId,
        isResendDisabled,
        getResendButtonText,
        previewProposalNumber,
        openResetNumbersModal,
        resettingNumbers,
        isSuperAdmin,
        isAdmin,
        proposalTemplates,
        templatesLoading,
        templatesSaving,
        saveProposalTemplates,
        resetProposalTemplates,
        companyPlaceholders,
        clientPlaceholders,
        proposalPlaceholders,
        blockPlaceholders,
      };
    },
  };
</script>

<style scoped lang="scss">
  .organization-settings {
    // Let the pill tab strip scroll horizontally instead of overflowing the
    // page when the tabs don't fit (e.g. on mobile). Scoped to this view so the
    // shared Tabs component and its other usages are untouched.
    &__tabs {
      // Horizontal-scroll behaviour now lives in the shared Tabs component.
    }

    &__members {
      margin-top: 2rem;
      border-top: 1px solid var(--slate-200);
    }

    &__form {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      padding: 0.5rem 0;
    }

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      margin-bottom: 0.875rem;

      &:last-child {
        margin-bottom: 0;
      }

      &--single {
        grid-template-columns: 1fr;
      }

      @media (max-width: 640px) {
        grid-template-columns: 1fr;
      }
    }

    &__field {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      label {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--slate-700);
      }

      input[type='time'] {
        padding: 0.5rem 0.625rem;
        border: 1px solid var(--slate-200);
        border-radius: 0.375rem;
        font-size: 0.8125rem;
        color: var(--slate-800);
        background: white;

        &:focus {
          outline: none;
          border-color: var(--brand-400);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
        }
      }
    }

    &__help {
      font-size: 0.75rem;
      color: var(--slate-500);
      margin: 0;
      line-height: 1.4;
    }
  }

  // VAT-basis radio cards in the "Prijzen in productcatalogus" section.
  // Two stacked cards with hover/checked states — feels more deliberate
  // than a plain toggle for a setting with two equally valid options.
  .vat-basis-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    max-width: 720px;

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  .vat-basis-option {
    display: flex;
    gap: 0.625rem;
    align-items: flex-start;
    padding: 0.875rem 1rem;
    border: 1px solid var(--slate-200);
    border-radius: 0.625rem;
    background: var(--white);
    cursor: pointer;
    transition:
      border-color 0.15s ease,
      background-color 0.15s ease,
      box-shadow 0.15s ease;

    &:hover {
      border-color: var(--slate-300);
    }

    input[type='radio'] {
      // The global reset (_reset.scss) strips appearance from all inputs,
      // which makes native radios invisible — restore it here.
      appearance: radio;
      -webkit-appearance: radio;
      flex-shrink: 0;
      width: 1rem;
      height: 1rem;
      margin: 0.1875rem 0 0;
      accent-color: var(--brand-500);
      cursor: pointer;
    }

    &__body {
      display: flex;
      flex-direction: column;
      gap: 0.1875rem;
      min-width: 0;
    }

    &__title {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__hint {
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    &--checked {
      border-color: var(--brand-400, #60a5fa);
      background: var(--brand-50);
      box-shadow: 0 0 0 1px var(--brand-300, #93c5fd);

      .vat-basis-option__title {
        color: var(--brand-700);
      }
    }
  }

  .team-section {
    margin-top: 2rem;
  }

  .member-list,
  .pending-invites {
    background: white;
    border-radius: 8px;
    padding: 1.5rem 0;
    margin-bottom: 2rem;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;

      h3 {
        margin: 0;
      }
    }
  }

  .member-item,
  .invite-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
    border-bottom: 1px solid #eee;

    &:last-child {
      border-bottom: none;
    }
  }

  .member-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: 0;

    > div {
      min-width: 0;
    }

    h4,
    p {
      overflow-wrap: anywhere;
    }
  }

  .member-hours-summary {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.125rem;
    font-size: 0.75rem;
    color: var(--slate-400);

    svg {
      width: 12px;
      height: 12px;
    }

    &--custom {
      color: var(--brand-600);
    }
  }

  .member-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .member-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
  }

  .member-role {
    flex-shrink: 0;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    background: var(--slate-100);
    color: var(--slate-700);

    &--admin {
      background: var(--purple-100);
      color: var(--purple-700);
    }

    &--member {
      background: var(--blue-100);
      color: var(--blue-700);
    }

    &--installer {
      background: var(--amber-100);
      color: var(--amber-700);
    }
  }

  // Same pill look as .member-role, but as an editable select with a caret.
  .member-role-select {
    flex-shrink: 0;
    appearance: none;
    -webkit-appearance: none;
    padding: 0.25rem 1.75rem 0.25rem 0.75rem;
    border: 1px solid transparent;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    background-color: var(--slate-100);
    color: var(--slate-700);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.5rem center;
    cursor: pointer;

    &:hover {
      border-color: var(--slate-300);
    }

    &:focus {
      outline: none;
      border-color: var(--brand-400, #60a5fa);
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }

    &--admin {
      background-color: var(--purple-100);
      color: var(--purple-700);
    }

    &--member {
      background-color: var(--blue-100);
      color: var(--blue-700);
    }

    &--installer {
      background-color: var(--amber-100);
      color: var(--amber-700);
    }
  }

  .invite-info {
    min-width: 0;

    p {
      overflow-wrap: anywhere;
    }
  }

  .invite-status {
    font-size: 0.875rem;
    color: #666;
  }

  .invite-actions {
    flex-shrink: 0;
    display: flex;
    gap: 0.5rem;
  }

  .proposal-numbering-preview {
    margin-top: 1.5rem;
    padding: 1rem;
    background: var(--cs-background-primary, #f9fafb);
    border: 1px solid var(--cs-input-default-border, #e5e7eb);
    border-radius: var(--cs-border-radius, 0.5rem);

    strong {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: var(--cs-text-body, #111827);
    }

    .preview-number {
      font-family: monospace;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--cs-brand-primary, #3b82f6);
    }
  }

  .proposal-numbering-actions {
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid var(--cs-input-default-border, #e5e7eb);

    .help-text {
      margin-top: 0.5rem;
      color: var(--cs-text-body, #6b7280);
    }
  }

  .help-text {
    font-size: 0.875rem;
    color: var(--cs-text-body, #6b7280);
    margin-top: 0.25rem;
    line-height: 1.4;

    &--error {
      color: var(--red-600, #dc2626);
    }

    &--warning {
      color: var(--amber-600, #d97706);
    }
  }

  .reminder-settings {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .attachment-list {
    list-style: none;
    margin: 0 0 0.75rem;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    max-width: 480px;

    &__item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--slate-50);
      color: var(--slate-700);
      font-size: 0.875rem;
    }

    &__name {
      flex: 1;
      color: var(--slate-700);
      text-decoration: none;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      @include hover {
        text-decoration: underline;
      }
    }

    &__remove {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border: none;
      background: none;
      border-radius: 4px;
      cursor: pointer;
      color: var(--slate-400);

      @include hover {
        background-color: var(--slate-200);
        color: var(--slate-600);
      }
    }

    &__input {
      display: none;
    }
  }

  .reminder-intervals {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 320px;

    &__label {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__input {
      width: 120px;
      padding: 0.5rem 0.75rem;
      font-size: 0.875rem;
      color: var(--slate-800);
      background: var(--white);
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;

      &:focus {
        outline: none;
        border-color: var(--brand-400, #60a5fa);
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
      }
    }
  }

  .templates-loading {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 2rem 0;
    color: var(--cs-text-body, #6b7280);
    font-size: 0.875rem;
  }

  .template-field {
    margin-bottom: 1.5rem;

    &__label {
      display: block;
      font-weight: 600;
      font-size: 0.875rem;
      color: var(--cs-text-body, #111827);
      margin-bottom: 0.25rem;
    }

    &__editor {
      width: 100%;
      margin-top: 0.5rem;
      padding: 0.75rem;
      font-family: 'SF Mono', 'Fira Code', 'Fira Mono', Menlo, Consolas, monospace;
      font-size: 0.8125rem;
      line-height: 1.5;
      color: var(--cs-text-body, #1f2937);
      background: var(--cs-background-primary, #f9fafb);
      border: 1px solid var(--cs-input-default-border, #e5e7eb);
      border-radius: var(--cs-border-radius, 0.5rem);
      resize: vertical;
      tab-size: 2;

      &:focus {
        outline: none;
        border-color: var(--cs-brand-primary, #3b82f6);
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
      }

      &--css {
        color: var(--purple-700, #7c3aed);
      }
    }
  }

  .template-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .workflow-sub-settings {
    margin-top: 0.75rem;
    padding: 1rem;
    background: var(--slate-50);
    border-radius: 0.5rem;
    border: 1px solid var(--slate-200);
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .template-placeholders {
    margin-top: 1rem;
    border: 1px solid var(--cs-input-default-border, #e5e7eb);
    border-radius: var(--cs-border-radius, 0.5rem);
    padding: 0.75rem 1rem;

    summary {
      cursor: pointer;
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--cs-text-body, #374151);
      user-select: none;
    }

    &__grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
      gap: 1.5rem;
      margin-top: 1rem;
    }

    &__group {
      h4 {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--cs-text-body, #6b7280);
        margin-bottom: 0.5rem;
      }

      code {
        display: block;
        font-size: 0.75rem;
        font-family: 'SF Mono', 'Fira Code', monospace;
        color: var(--cs-text-body, #374151);
        padding: 0.125rem 0;
        line-height: 1.6;
      }
    }
  }
</style>
